import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { stringifyGraph } from '@/clients/http'
import { CouponModel } from '@/http/models/Coupon'
import { CustomerModel } from '@/http/models/customer'
import { OrderModel } from '@/http/models/Order'
import { ProductModel as ProductApiClient } from '@/http/models/Product/Product'

export interface SearchResultItem {
  id: string
  label: string
  sub?: string
  route: string
  badge?: string
}

export interface SearchResults {
  orders: SearchResultItem[]
  products: SearchResultItem[]
  customers: SearchResultItem[]
  coupons: SearchResultItem[]
}

/** Results per group. */
const LIMIT = 5
const MIN_CHARS = 2

const EMPTY: SearchResults = { orders: [], products: [], customers: [], coupons: [] }

interface ListResponse<T> {
  success: boolean
  data?: { result?: T[] } | null
}

const rows = <T,>(res: ListResponse<T>): T[] => (res.success ? res.data?.result ?? [] : [])

interface OrderRow { _id: string, order_ref?: string, status?: string, customer_info?: { name?: string, phone?: string } }
interface ProductRow { _id: string, name?: string, variations?: { sku?: string }[] }
interface CustomerRow { _id: string, name?: string, phone?: string, email?: string }
interface CouponRow { _id: string, coupon_code?: string, discount_type?: string, discount?: number }

const searchOrders = async (search: string): Promise<SearchResultItem[]> => {
  const res = await OrderModel.getList({
    query: { search, page: 1, length: LIMIT, graph_enc: stringifyGraph({ _id: true, order_ref: true, status: true, customer_info: true }) },
  })
  return rows(res as ListResponse<OrderRow>).map((o) => ({
    id: o._id,
    label: `#${o.order_ref ?? o._id.slice(-6)}`,
    sub: [o.customer_info?.name, o.customer_info?.phone].filter(Boolean).join(' · '),
    badge: o.status,
    route: `/orders/${o._id}`,
  }))
}

const searchProducts = async (search: string): Promise<SearchResultItem[]> => {
  const res = await ProductApiClient.getList({
    query: { search, page: 1, length: LIMIT, graph_enc: stringifyGraph({ _id: true, name: true, variations: { sku: true } }) },
  })
  return rows(res as ListResponse<ProductRow>).map((p) => ({
    id: p._id,
    label: p.name ?? '',
    sub: p.variations?.find((v) => v.sku)?.sku ?? '',
    route: `/products/${p._id}`,
  }))
}

const searchCustomers = async (search: string): Promise<SearchResultItem[]> => {
  const res = await CustomerModel.getList({
    query: { search, page: 1, length: LIMIT, graph_enc: stringifyGraph({ _id: true, name: true, phone: true, email: true }) },
  })
  return rows(res as ListResponse<CustomerRow>).map((c) => ({
    id: c._id,
    label: c.name || c.phone || '',
    sub: c.phone && c.name ? c.phone : c.email ?? '',
    route: `/customers/${c._id}`,
  }))
}

const searchCoupons = async (search: string): Promise<SearchResultItem[]> => {
  const res = await CouponModel.getList({
    query: { search, page: 1, length: LIMIT, graph_enc: stringifyGraph({ _id: true, coupon_code: true, discount_type: true, discount: true }) },
  })
  return rows(res as ListResponse<CouponRow>).map((c) => ({
    id: c._id,
    label: c.coupon_code ?? '',
    sub: c.discount === undefined ? '' : c.discount_type === 'percentage' ? `${c.discount}% off` : `৳${c.discount} off`,
    route: '/coupons',
  }))
}

const settled = (r: PromiseSettledResult<SearchResultItem[]>): SearchResultItem[] => (r.status === 'fulfilled' ? r.value : [])

/**
 * Searches orders, products, customers and coupons at once for the command palette: debounced, at least
 * MIN_CHARS characters, cached per query; a group that fails is shown empty, the others still show (`failed`
 * tells the palette that something went wrong).
 */
export function useCommandSearch(query: string, delay = 250) {
  const [debounced, setDebounced] = useState(query.trim())

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), delay)
    return () => clearTimeout(id)
  }, [query, delay])

  const enabled = debounced.length >= MIN_CHARS
  const { data, isFetching } = useQuery({
    queryKey: ['command-search', debounced],
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const groups = await Promise.allSettled([searchOrders(debounced), searchProducts(debounced), searchCustomers(debounced), searchCoupons(debounced)])
      const [orders, products, customers, coupons] = groups.map(settled)
      return { results: { orders, products, customers, coupons }, failed: groups.some((g) => g.status === 'rejected') }
    },
  })

  const pending = query.trim() !== debounced
  return {
    results: enabled ? data?.results ?? EMPTY : EMPTY,
    loading: query.trim().length >= MIN_CHARS && (pending || isFetching),
    failed: enabled && Boolean(data?.failed),
    tooShort: query.trim().length > 0 && query.trim().length < MIN_CHARS,
  }
}
